import {
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  IconButton,
  Input,
  NativeSelect,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { z } from "zod"

import {
  Providers,
  type ProviderClientPublic,
  type ProviderPublic,
  type ProviderUserPublic,
  type UserPublic,
  Users,
} from "@/client"
import AddUser from "@/components/Users/AddUser"
import EditUser from "@/components/Users/EditUser"
import DeleteUser from "@/components/Users/DeleteUser"
import PendingUsers from "@/components/Users/PendingUsers"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"

const usersSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 5

function getUsersQueryOptions({
  page,
  search,
  providerId,
}: {
  page: number
  search: string
  providerId: string
}) {
  return {
    queryFn: () =>
      Users.readUsers({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        search: search || undefined,
        providerId: providerId || undefined,
      } as any),
    queryKey: ["users", { page, search, providerId }],
  }
}

async function fetchJson<T>(path: string): Promise<T> {
  const { getStoredAccessToken } = await import("@/utils/auth")
  const token = getStoredAccessToken() || ""
  const baseURL = import.meta.env.VITE_API_URL
  const res = await fetch(`${baseURL}/api/v1${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<T>
}

function useProviderMemberships(userId: string, enabled: boolean) {
  const providerUsersQ = useQuery<{ data: ProviderUserPublic[]; count: number }>({
    queryKey: ["providerUsers", "for-user", userId],
    queryFn: () => fetchJson(`/provider-users/for-user/${userId}`),
    enabled,
  })
  const providerClientsQ = useQuery<{
    data: ProviderClientPublic[]
    count: number
  }>({
    queryKey: ["providerClients", "for-user", userId],
    queryFn: () => fetchJson(`/provider-clients/for-user/${userId}`),
    enabled,
  })
  return { providerUsersQ, providerClientsQ }
}

function MembershipDetail({ userId }: { userId: string }) {
  const { providerUsersQ, providerClientsQ } = useProviderMemberships(userId, true)

  const isLoading = providerUsersQ.isLoading || providerClientsQ.isLoading
  const workerRoles = providerUsersQ.data?.data ?? []
  const clientRoles = providerClientsQ.data?.data ?? []

  if (isLoading) {
    return (
      <Box px={4} py={2}>
        <Spinner size="sm" color="blue.400" />
      </Box>
    )
  }

  if (workerRoles.length === 0 && clientRoles.length === 0) {
    return (
      <Box px={4} py={2}>
        <Text fontSize="sm" color="gray.400" fontStyle="italic">
          Not linked to any provider.
        </Text>
      </Box>
    )
  }

  return (
    <Box px={4} py={3} bg="gray.50" borderTopWidth="1px" borderColor="gray.100">
      {workerRoles.length > 0 && (
        <Box mb={clientRoles.length > 0 ? 3 : 0}>
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wide"
            mb={1}
          >
            Provider Staff
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {workerRoles.map((pu) => (
              <Badge key={pu.id} colorPalette="blue" variant="subtle" size="sm">
                {pu.providerId}
                {pu.isProviderAdmin && (
                  <Text as="span" ml={1} opacity={0.7}>
                    (admin)
                  </Text>
                )}
              </Badge>
            ))}
          </Flex>
        </Box>
      )}
      {clientRoles.length > 0 && (
        <Box>
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wide"
            mb={1}
          >
            Provider Clients
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {clientRoles.map((pc) => (
              <Badge key={pc.id} colorPalette="teal" variant="subtle" size="sm">
                {pc.providerName ?? pc.providerId}
                {pc.providerGroupName && (
                  <Text as="span" ml={1} opacity={0.7}>
                    · {pc.providerGroupName}
                  </Text>
                )}
              </Badge>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  )
}

export const Route = createFileRoute("/_layout/users")({
  component: Admin,
  validateSearch: (search) => usersSearchSchema.parse(search),
})

function UsersTable() {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const navigate = useNavigate({ from: Route.fullPath })
  const { page } = Route.useSearch()

  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [providerId, setProviderId] = useState("")
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: providersData } = useQuery({
    queryKey: ["providers", "all"],
    queryFn: () => Providers.readProviders({ query: { skip: 0, limit: 200 } }),
  })
  const providers: ProviderPublic[] = providersData?.data ?? []

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getUsersQueryOptions({ page, search, providerId }),
    placeholderData: (prevData) => prevData,
  })

  const setPage = (page: number) =>
    navigate({ search: (prev: { page: number }) => ({ ...prev, page }) })

  const handleSearch = () => {
    setSearch(searchInput)
    setPage(1)
  }

  const handleProviderChange = (val: string) => {
    setProviderId(val)
    setPage(1)
  }

  const users = data?.data.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0

  if (isLoading && !isPlaceholderData) {
    return <PendingUsers />
  }

  return (
    <>
      {/* Search + filter bar */}
      <Flex gap={3} mb={4} mt={4} align="center">
        <Input
          placeholder="Search name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          maxW="320px"
          size="sm"
        />
        <NativeSelect.Root size="sm" maxW="260px">
          <NativeSelect.Field
            value={providerId}
            onChange={(e) => handleProviderChange(e.target.value)}
          >
            <option value="">All providers</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.entityName ?? p.id}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {(search || providerId) && (
          <Text
            fontSize="sm"
            color="blue.500"
            cursor="pointer"
            textDecoration="underline"
            onClick={() => {
              setSearch("")
              setSearchInput("")
              setProviderId("")
              setPage(1)
            }}
          >
            Clear
          </Text>
        )}
      </Flex>

      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader w="32px" />
            <Table.ColumnHeader>Full Name</Table.ColumnHeader>
            <Table.ColumnHeader>Email</Table.ColumnHeader>
            <Table.ColumnHeader>Role</Table.ColumnHeader>
            <Table.ColumnHeader>Status</Table.ColumnHeader>
            <Table.ColumnHeader w="80px" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {users.length === 0 && !isLoading ? (
            <Table.Row>
              <Table.Cell colSpan={6} textAlign="center" py={8} color="gray.400">
                No users found.
              </Table.Cell>
            </Table.Row>
          ) : (
            users.map((user) => {
              const isExpanded = expandedId === user.id
              return (
                <>
                  <Table.Row
                    key={user.id}
                    opacity={isPlaceholderData ? 0.5 : 1}
                    _hover={{ bg: "gray.50" }}
                  >
                    <Table.Cell>
                      <IconButton
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        size="xs"
                        variant="ghost"
                        onClick={() => setExpandedId(isExpanded ? null : user.id)}
                      >
                        <Text fontSize="10px" lineHeight="1">
                          {isExpanded ? "▼" : "▶"}
                        </Text>
                      </IconButton>
                    </Table.Cell>
                    <Table.Cell fontWeight="medium">
                      {user.full_name || (
                        <span style={{ color: "#A0AEC0" }}>—</span>
                      )}
                      {currentUser?.id === user.id && (
                        <Badge ml="2" colorPalette="teal" size="sm">
                          You
                        </Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell
                      color={!user.email ? "gray.400" : "inherit"}
                      truncate
                      maxW="sm"
                    >
                      {user.email || "—"}
                    </Table.Cell>
                    <Table.Cell>
                      {user.is_superuser ? (
                        <Badge colorPalette="purple" size="sm">
                          Superuser
                        </Badge>
                      ) : (
                        <Badge colorPalette="gray" variant="outline" size="sm">
                          User
                        </Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={user.is_active ? "green" : "gray"}
                        size="sm"
                      >
                        {user.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap={1}>
                        <EditUser user={user} iconOnly />
                        <DeleteUser id={user.id} iconOnly />
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                  {isExpanded && (
                    <Table.Row key={`${user.id}-detail`}>
                      <Table.Cell colSpan={6} p={0}>
                        <MembershipDetail userId={user.id} />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </>
              )
            })
          )}
        </Table.Body>
      </Table.Root>

      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function Admin() {
  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        Users Management
      </Heading>

      <AddUser />
      <UsersTable />
    </Container>
  )
}
